import React, { useState, useRef } from "react";
import { Modal, View, Text, TextInput, TouchableOpacity, ActivityIndicator, Platform, KeyboardAvoidingView, ScrollView, Dimensions } from "react-native";
import { useCrew } from "@/context/CrewProvider";
import { trpc } from "@/trpc/react";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import colors from "@/constants/Colors";
import { ChatInput } from "./chat-input";

interface Attachment {
    uri: string;
    name: string;
    type: string;
    size?: number;
}

interface NewConversationModalProps {
    visible: boolean;
    onClose: () => void;
}

export function NewConversationModal({ visible, onClose }: NewConversationModalProps) {
    const [conversationName, setConversationName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const { currentCrewMember } = useCrew();
    const router = useRouter();
    const conversationNameInputRef = useRef<TextInput>(null);
    const windowHeight = Dimensions.get('window').height;

    // Clear form when modal opens
    React.useEffect(() => {
        if (visible) {
            setConversationName("");
            setError(null);
            setAttachments([]);
            // Focus the conversation name input when modal opens
            setTimeout(() => {
                conversationNameInputRef.current?.focus();
            }, 100);
        }
    }, [visible]);

    // Create conversation mutation
    const { mutate: createConversation } = trpc.messages.createCrewInitiatedConversation.useMutation({
        onSuccess: (data) => {
            setIsSubmitting(false);
            onClose();
            // Navigate to the new conversation
            if (data && data.id) {
                router.push(`/(app)/messages/${data.id}`);
            }
        },
        onError: (err) => {
            console.error("Failed to create conversation:", err);
            setIsSubmitting(false);
            setError(err.message || "Failed to create conversation. Please try again.");
        }
    });

    const handleCreateConversation = async (content: string, attachmentsArray?: Attachment[]) => {
        if (!currentCrewMember?.id) {
            setError("You must be logged in to create a conversation.");
            return;
        }

        if (!conversationName.trim()) {
            setError("Please enter a conversation name.");
            return;
        }

        if (!content.trim() && (!attachmentsArray || attachmentsArray.length === 0)) {
            setError("Please enter a message or add attachments.");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // If there are attachments, we need to upload them first
            let uploadedAttachments: Attachment[] = [];

            if (attachmentsArray && attachmentsArray.length > 0) {
                uploadedAttachments = attachmentsArray;
            }

            // Create conversation
            createConversation({
                name: conversationName.trim(),
                crew_id: currentCrewMember.id,
                initial_message: content,
                attachments: uploadedAttachments.length > 0 ? uploadedAttachments.map(att => ({
                    file_path: att.uri,
                    fileName: att.name,
                    fileType: att.type,
                    fileSize: att.size || 0
                })) : undefined
            });
        } catch (err) {
            console.error("Error creating conversation:", err);
            setIsSubmitting(false);
            setError("Failed to create conversation. Please try again.");
        }
    };

    if (!visible) return null;

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
            onRequestClose={onClose}
        >
            {/* Full screen modal background */}
            <View style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)'
            }}>
                <KeyboardAvoidingView
                    behavior={Platform.OS === "ios" ? "padding" : "height"}
                    style={{ flex: 1 }}
                    keyboardVerticalOffset={Platform.OS === "ios" ? 30 : 0}
                >
                    <View style={{ flex: 1, justifyContent: 'center' }}>
                        {/* Modal content card */}
                        <View
                            style={{
                                backgroundColor: 'white',
                                borderRadius: 12,
                                margin: 16,
                                overflow: 'hidden',
                                maxHeight: windowHeight * 0.6
                            }}
                        >
                            {/* Header */}
                            <View style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 16,
                                borderBottomWidth: 1,
                                borderBottomColor: '#e5e5e5'
                            }}>
                                <Text style={{ fontSize: 18, fontWeight: '600' }}>New Conversation</Text>
                                <TouchableOpacity onPress={onClose} disabled={isSubmitting}>
                                    <Ionicons name="close" size={24} color="#666" />
                                </TouchableOpacity>
                            </View>

                            {/* Scrollable Content */}
                            <ScrollView style={{ padding: 16 }}>
                                {/* Conversation Name Input */}
                                <View style={{ marginBottom: 16 }}>
                                    <Text style={{ fontSize: 14, fontWeight: '500', marginBottom: 4, color: '#4b5563' }}>
                                        Conversation Name
                                    </Text>
                                    <TextInput
                                        ref={conversationNameInputRef}
                                        style={{
                                            padding: 12,
                                            borderRadius: 6,
                                            borderWidth: 1,
                                            borderColor: '#d1d5db',
                                            backgroundColor: 'white'
                                        }}
                                        placeholder="Enter a name for this conversation"
                                        value={conversationName}
                                        onChangeText={setConversationName}
                                        autoCapitalize="sentences"
                                        editable={!isSubmitting}
                                        maxLength={100}
                                    />
                                </View>

                                {/* First Message */}
                                <View style={{ marginBottom: 16 }}>
                                    <Text style={{ fontSize: 14, fontWeight: '500', marginBottom: 4, color: '#4b5563' }}>
                                        First Message
                                    </Text>
                                    <Text style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                                        This will be sent when the conversation is created
                                    </Text>
                                </View>

                                {/* Error display */}
                                {error && (
                                    <View style={{
                                        marginBottom: 16,
                                        padding: 12,
                                        backgroundColor: '#fee2e2',
                                        borderRadius: 6
                                    }}>
                                        <Text style={{ color: '#dc2626' }}>{error}</Text>
                                    </View>
                                )}

                                {/* Add extra padding at the bottom to ensure content is visible when keyboard is open */}
                                <View style={{ height: 100 }} />
                            </ScrollView>

                            {/* Chat Input - Fixed at the bottom */}
                            <View style={{
                                borderTopWidth: 1,
                                borderTopColor: '#e5e5e5',
                                backgroundColor: 'white'
                            }}>
                                <ChatInput
                                    onSendMessage={handleCreateConversation}
                                    disabled={isSubmitting}
                                    placeholder="Type your first message..."
                                />
                                {isSubmitting && (
                                    <View style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <ActivityIndicator size="large" color={colors.primary} />
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                </KeyboardAvoidingView>
            </View>
        </Modal>
    );
} 